<template>
  <iColumn
    class="video-container"
    height="fill"
    width="fill"
    gap="none"
    wrap="nowrap"
  >
    <!-- Main video area -->
    <iRow
      height="fill"
      width="fill"
      :min-height="0"
      align="center"
      gap="none"
    >
      <iOverlay v-if="mainVideoStream" width="hug">
        <video
          ref="mainVideo"
          class="main-video"
          autoplay
          disablePictureInPicture
          :muted="mainVideoStream === localStream"
        />
        <template #overlay>
          <iRow
            width="fill"
            height="fill"
            vertical-align="bottom"
            align="center"
          >
            <iRow
              ref="controls"
              class="controls-overlay"
              align="evenly"
              :width="300"
              vertical-align="middle"
            >
              <iButton variant="tertiary" @click="toggleCamera">
                <iIcon variant="primary" :icon="cameraEnabled ? 'camera' : 'camera-slash'" />
              </iButton>
              <iButton variant="tertiary" @click="toggleMicrophone">
                <iIcon variant="primary" :icon="microphoneEnabled ? 'microphone' : 'microphone-slash'" />
              </iButton>
              <iButton v-if="!inCall" variant="tertiary" @click="joinCall">
                <iIcon variant="primary" icon="door-open" />
              </iButton>
              <iButton v-if="inCall" variant="tertiary" @click="leaveCall">
                <iIcon variant="primary" icon="arrow-right-from-bracket" />
              </iButton>
            </iRow>
          </iRow>
        </template>
      </iOverlay>
      <iRow
        v-else
        class="no-video"
        width="fill"
        height="fill"
        vertical-align="middle"
        align="center"
      >
        <iText>No video selected</iText>
      </iRow>
    </iRow>
    <iRow
      :min-height="118"
      :max-height="160"
      width="fill"
      overflow="scroll"
      gap="extraSmall"
    >
      <iRow
        :width="210"
        height="fill"
        class="video-tile"
        :class="{active: mainVideoStream === localStream}"
        @click="setMainVideo(localStream, 'local')"
      >
        <video
          ref="localVideo"
          class="participant-video"
          autoplay
          disablePictureInPicture
          muted
        />
        <iRow
          width="fill"
          height="hud"
          align="center"
          vertical-align="middle"
          class="video-label"
        >
          <iText>You</iText>
        </iRow>
        <div class="video-controls">
          <span class="control-indicator">
            <iIcon
              v-if="!cameraEnabled"
              variant="primary"
              icon="camera-slash"
              :size="20"
            />
            <iIcon
              v-if="!microphoneEnabled"
              variant="primary"
              icon="microphone-slash"
              :size="20"
            />
          </span>
        </div>
      </iRow>

      <iRow
        v-for="(participant, peerId) in remoteParticipants"
        :key="peerId"
        :width="210"
        height="fill"
        class="video-tile"
        :class="{active: mainVideoStream === participant.stream}"
        @click="setMainVideo(participant.stream, peerId)"
      >
        <video
          :ref="`remoteVideo_${peerId}`"
          class="participant-video"
          disablePictureInPicture
          autoplay
        />
        <iRow
          width="fill"
          height="hud"
          align="center"
          vertical-align="middle"
          class="video-label"
        >
          <iText>Participant {{ peerId }}</iText>
        </iRow>
      </iRow>
    </iRow>
  </iColumn>
</template>

<script>
import { mapState } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { useConversationStore } from "%/stores/conversation-store";

export default {
  name: "GroupVideoPage",
  props: {
    roomIdentifier: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      localStream: null,
      remoteParticipants: {}, // { peerId: { stream, peerConnection, userId } }
      peerConnections: {}, // { peerId: RTCPeerConnection }
      signalingSocket: null,
      inCall: false,
      cameraEnabled: true,
      microphoneEnabled: true,
      controlsVisible: true,
      hideControlsTimeout: null,
      mainVideoStream: null,
      mainVideoPeerId: null,
      orgName: "Multi-Party Video Call",
    };
  },
  computed: {
    ...mapState(useConversationStore, ["conversationId"]),
    ...mapState(useGeneralStore, ["userId"]),
  },
  mounted() {
    this.setupWebSocket();
    this.getUserMedia();
  },
  beforeUnmount() {
    this.leaveCall();
  },
  methods: {
    handleMouseMove() {
      this.controlsVisible = true;
      this.resetHideControlsTimeout();
    },
    resetHideControlsTimeout() {
      clearTimeout(this.hideControlsTimeout);
      this.hideControlsTimeout = setTimeout(() => {
        this.controlsVisible = false;
      }, 3000);
    },
    setMainVideo(stream, peerId) {
      this.mainVideoStream = stream;
      this.mainVideoPeerId = peerId;
      this.$nextTick(() => {
        if (this.$refs.mainVideo) {
          this.$refs.mainVideo.srcObject = stream;
        }
      });
    },
    setupWebSocket() {
      this.signalingSocket = new WebSocket("wss://creator-ws.inspiration.co:9090");

      this.signalingSocket.onopen = () => {
        // eslint-disable-next-line no-console
        console.log("Connected to signaling server.");
      };

      this.signalingSocket.onmessage = event => {
        const message = JSON.parse(event.data);
        // eslint-disable-next-line no-console
        console.log("Received message:", message);

        if (message.existingPeers) {
          this.handleExistingPeers(message.existingPeers);
        } else if (message.newPeer) {
          this.handleNewPeer(message.peerId);
        } else if (message.offer) {
          this.handleOffer(message.offer, message.fromPeer);
        } else if (message.answer) {
          this.handleAnswer(message.answer, message.fromPeer);
        } else if (message.iceCandidate) {
          this.handleNewICECandidate(message.iceCandidate, message.fromPeer);
        } else if (message.peerDisconnected) {
          this.handlePeerDisconnected(message.peerId);
        }
      };

      this.signalingSocket.onclose = () => {
        // eslint-disable-next-line no-console
        console.log("Disconnected from signaling server");
      };
    },
    handleExistingPeers(peerIds) {
      peerIds.forEach(peerId => {
        this.createPeerConnection(peerId);
        this.createOffer(peerId);
      });
    },
    handleNewPeer(peerId) {
      this.createPeerConnection(peerId);
    },
    handlePeerDisconnected(peerId) {
      if (this.peerConnections[peerId]) {
        this.peerConnections[peerId].close();
        delete this.peerConnections[peerId];
      }

      if (this.remoteParticipants[peerId]) {
        if (this.mainVideoPeerId === peerId) {
          this.setMainVideo(this.localStream, "local");
        }
        delete this.remoteParticipants[peerId];
      }
    },
    toggleCamera() {
      if (this.localStream) {
        const videoTrack = this.localStream.getTracks().find(track => track.kind === "video");
        if (videoTrack) {
          videoTrack.enabled = !videoTrack.enabled;
          this.cameraEnabled = videoTrack.enabled;
        }
      }
    },
    toggleMicrophone() {
      if (this.localStream) {
        const audioTrack = this.localStream.getTracks().find(track => track.kind === "audio");
        if (audioTrack) {
          audioTrack.enabled = !audioTrack.enabled;
          this.microphoneEnabled = audioTrack.enabled;
        }
      }
    },
    async getUserMedia() {
      try {
        this.localStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        if (this.$refs.localVideo) {
          this.$refs.localVideo.srcObject = this.localStream;
        }

        if (!this.mainVideoStream) {
          this.setMainVideo(this.localStream, "local");
        }

        Object.values(this.peerConnections).forEach(pc => {
          this.localStream.getTracks().forEach(track => {
            pc.addTrack(track, this.localStream);
          });
        });

      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error accessing media devices:", err);
      }
    },
    createPeerConnection(peerId) {
      const configuration = {
        iceServers: [
          {
            urls: "turn:turn.inspiration.co:3478",
            username: "test",
            credential: "Test@123",
          },
        ],
      };

      const peerConnection = new RTCPeerConnection(configuration);
      this.peerConnections[peerId] = peerConnection;

      peerConnection.onicecandidate = event => {
        if (event.candidate) {
          this.sendMessage({
            iceCandidate: event.candidate,
            targetPeer: peerId,
            roomId: this.roomIdentifier,
          });
        }
      };

      peerConnection.ontrack = event => {
        // eslint-disable-next-line no-console
        console.log("Received remote stream from peer:", peerId);
        const remoteStream = event.streams[0];

        this.remoteParticipants[peerId] = {
          stream: remoteStream,
          peerConnection: peerConnection,
        };

        this.$nextTick(() => {
          const videoRef = this.$refs[`remoteVideo_${peerId}`];
          if (videoRef && videoRef[0]) {
            videoRef[0].srcObject = remoteStream;
          }
        });
      };

      peerConnection.onconnectionstatechange = () => {
        // eslint-disable-next-line no-console
        console.log(`Peer ${peerId} connection state:`, peerConnection.connectionState);
      };

      // Add local stream tracks if available
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => {
          peerConnection.addTrack(track, this.localStream);
        });
      }
    },
    async createOffer(peerId) {
      try {
        const peerConnection = this.peerConnections[peerId];
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);

        this.sendMessage({
          offer: offer,
          targetPeer: peerId,
          roomId: this.roomIdentifier,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error creating offer for peer", peerId, ":", err);
      }
    },
    async handleOffer(offer, fromPeer) {
      try {
        if (!this.peerConnections[fromPeer]) {
          this.createPeerConnection(fromPeer);
        }

        const peerConnection = this.peerConnections[fromPeer];
        await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);

        this.sendMessage({
          answer: answer,
          targetPeer: fromPeer,
          roomId: this.roomIdentifier,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error handling offer from peer", fromPeer, ":", err);
      }
    },
    async handleAnswer(answer, fromPeer) {
      try {
        const peerConnection = this.peerConnections[fromPeer];
        if (peerConnection) {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error handling answer from peer", fromPeer, ":", err);
      }
    },
    async handleNewICECandidate(candidate, fromPeer) {
      try {
        const peerConnection = this.peerConnections[fromPeer];
        if (peerConnection) {
          await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error adding ICE candidate from peer", fromPeer, ":", err);
      }
    },
    sendMessage(message) {
      if (this.signalingSocket && this.signalingSocket.readyState === WebSocket.OPEN) {
        this.signalingSocket.send(JSON.stringify(message));
      }
    },
    joinCall() {
      this.inCall = true;

      this.sendMessage({
        roomId: this.roomIdentifier,
        join: true,
        userId: this.userId,
      });
    },
    leaveCall() {
      this.inCall = false;

      Object.values(this.peerConnections).forEach(pc => pc.close());
      this.peerConnections = {};
      this.remoteParticipants = {};

      this.sendMessage({
        leaveRoom: true,
        roomId: this.roomIdentifier,
      });

      if (this.signalingSocket) {
        this.signalingSocket.close();
      }
      this.setupWebSocket();

      this.setMainVideo(this.localStream, "local");
    },
  },
};
</script>

<style scoped>
.main-video {
  max-width: 100%;
  height: 100%;
  aspect-ratio: 16/9;
  object-fit: cover;
}

.no-video {
  width: 100%;
  height: 100%;
}

.video-tile {
  position: relative;
  width: 120px;
  height: 90px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s;
}

.video-tile.active {
  border-color: #6eff5d;
}

.video-tile:hover {
  border-color: #55b647;
}

.participant-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  padding: 2px 5px;
}

.video-controls {
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  gap: 2px;
}

.control-indicator {
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
}

.controls-overlay {
  margin-bottom: 1vh;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 25px;
  padding: 10px 20px;
  transition: opacity 0.3s;

  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }
}
</style>
